import {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  type DocumentNode,
  type WatchQueryFetchPolicy,
} from "@apollo/client";
import { useApolloClient } from "@apollo/client/react";
import type { Omittable } from "@salestrak/ui";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type Connection<TNode> = Omittable<{
  edges: Array<{ node: TNode }>;
  pageInfo: { hasNextPage: boolean };
  total?: number | null;
}>;

export type UsePagedConnectionOptions<TData, TQuery> = Omittable<{
  /** The query document. `TQuery` is its generated result type, named by
   *  the caller beside it, so `getConnection` and `onData` read the result
   *  without a cast. The hook appends `skip` and `limit` to the variables,
   *  which is why the document is not typed on its variables here. */
  document: DocumentNode;
  /** Variables WITHOUT `skip` / `limit` — those are managed internally. */
  variables: Record<string, unknown>;
  getConnection: (data: TQuery) => Connection<TData> | undefined | null;
  /**
   * Called once per query result with the raw query data, so callers can
   * pull out sibling fields (e.g. `unreadCount`, `notes`, `activityDates`).
   */
  onData?: (rawQueryData: TQuery) => void;
  pageSize?: number;
  fetchPolicy?: WatchQueryFetchPolicy;
  /** Pause the hook (no subscription, no fetch). Used for `skip:!issueId`. */
  skipQuery?: boolean;
}>;

export type UsePagedConnectionResult<TData> = {
  items: TData[];
  total: number;
  hasMore: boolean;
  loading: boolean;
  loadingMore: boolean;
  /** Resolves when the page lands in the cache (or is already cached). */
  fetchPage: (offset: number, limit?: number) => Promise<void>;
  /** Drops every cached page and re-reads the first one. Resolves when that
   *  page lands, so a caller that awaits it is looking at fresh data. */
  refetch: () => Promise<void>;
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Offset-paginated connection hook with a per-page cache.
 *
 * Mirrors the DataTable's `pageCacheRef` pattern so flat lists (inbox,
 * activity feed) get the same growing-list semantics that DataTable groups
 * already have, without piggy-backing on Apollo's deleted offset-merge.
 *
 * Each `fetchPage(offset)` call swaps the active watchQuery to variables
 * `{ ...variables, skip: offset, limit }`. The result lands in
 * `pageCacheRef[offset]`. `items` is the concatenation of pages in
 * ascending offset order.
 */
export function usePagedConnection<TData, TQuery>({
  document,
  variables,
  getConnection,
  onData,
  pageSize = 50,
  fetchPolicy = "cache-and-network",
  skipQuery = false,
}: UsePagedConnectionOptions<TData, TQuery>): UsePagedConnectionResult<TData> {
  const client = useApolloClient();

  // Stable variables key for context-change detection.
  const variablesKey = useMemo(
    () => JSON.stringify(variables) + ":" + pageSize,
    [variables, pageSize],
  );
  const prevVariablesKeyRef = useRef(variablesKey);

  // Per-page cache: pageStart (absolute offset) -> mapped rows.
  const pageCacheRef = useRef(new Map<number, TData[]>());
  // Pending fetch resolvers, keyed by skip — resolved when the page lands.
  const pendingResolversRef = useRef(new Map<number, Array<() => void>>());
  // Bumped after each successful page fetch so memoized `items` recomputes.
  const [cacheVersion, setCacheVersion] = useState(0);

  const [activeSkip, setActiveSkip] = useState(0);
  const [loading, setLoading] = useState(true);
  // True once ANY result for the current variables has landed — a cache hit
  // counts, an empty connection counts. This is what separates "nothing known
  // yet" (skeleton) from "known to be empty" (empty state): `loading` alone
  // can't, because cache-and-network stays loading until the NETWORK confirms,
  // long after a cached answer already painted.
  const [settled, setSettled] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  // Connection metadata from the most recent successful fetch.
  const [meta, setMeta] = useState<{ total: number; hasMore: boolean }>({
    total: 0,
    hasMore: false,
  });
  const [refetchVersion, setRefetchVersion] = useState(0);

  // Reset cache when the underlying query context changes (filter/sort/etc).
  // Render-phase state adjustment (the sanctioned "derive state from props"
  // pattern): the version bump makes `items` recompute EMPTY on this very
  // render, so the outgoing context's rows never paint one stale frame under
  // the new one.
  if (prevVariablesKeyRef.current !== variablesKey) {
    prevVariablesKeyRef.current = variablesKey;
    pageCacheRef.current.clear();
    for (const list of pendingResolversRef.current.values()) {
      for (const r of list) r();
    }
    pendingResolversRef.current.clear();
    if (activeSkip !== 0) setActiveSkip(0);
    setMeta({ total: 0, hasMore: false });
    setCacheVersion((v) => v + 1);
    setSettled(false);
  }

  const onDataRef = useRef(onData);
  onDataRef.current = onData;
  const getConnectionRef = useRef(getConnection);
  getConnectionRef.current = getConnection;

  // Active watchQuery — re-subscribes whenever variables / activeSkip change.
  // LAYOUT effect on purpose: `watchQuery.subscribe` delivers a complete cache
  // hit synchronously, so subscribing before paint lets a cached answer (rows
  // OR a known-empty list) land in the same frame as the context switch —
  // a passive effect paints one skeleton frame first, which reads as a flash
  // when flipping between two already-visited tabs.
  useLayoutEffect(() => {
    if (skipQuery) {
      setLoading(false);
      return;
    }
    const isInitial = activeSkip === 0;
    if (isInitial) setLoading(true);
    else setLoadingMore(true);

    const fetchedForSkip = activeSkip;
    const observable = client.watchQuery<TQuery>({
      query: document,
      variables: { ...variables, skip: fetchedForSkip, limit: pageSize },
      fetchPolicy,
    });

    const subscription = observable.subscribe({
      next: (result) => {
        // Only a complete result is the query's declared shape; a partial
        // cache read is not a page.
        if (result.dataState === "complete") {
          onDataRef.current?.(result.data);
          const connection = getConnectionRef.current(result.data);
          if (connection?.edges) {
            setSettled(true);
            // The row IS the connection node — no re-shaping between the query
            // and the consumer.
            const rows = connection.edges.map((edge) => edge.node);
            pageCacheRef.current.set(fetchedForSkip, rows);
            setMeta({
              total: connection.total ?? 0,
              hasMore: connection.pageInfo?.hasNextPage ?? false,
            });
            setCacheVersion((v) => v + 1);
            const resolvers = pendingResolversRef.current.get(fetchedForSkip);
            if (resolvers) {
              pendingResolversRef.current.delete(fetchedForSkip);
              for (const r of resolvers) r();
            }
          }
        }
        if (!result.loading) {
          setLoading(false);
          setLoadingMore(false);
        }
      },
      error: () => {
        setLoading(false);
        setLoadingMore(false);
      },
    });

    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- variablesKey stands for the variables object contents
  }, [
    client,
    document,
    variablesKey,
    activeSkip,
    pageSize,
    fetchPolicy,
    skipQuery,
    refetchVersion,
  ]);

  const items = useMemo(() => {
    const offsets = Array.from(pageCacheRef.current.keys()).sort(
      (a, b) => a - b,
    );
    const out: TData[] = [];
    for (const off of offsets) {
      const rows = pageCacheRef.current.get(off);
      if (rows) out.push(...rows);
    }
    return out;
    // cacheVersion is the trigger — pageCacheRef itself is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- cacheVersion is the trigger; the cache ref is stable
  }, [cacheVersion]);

  const fetchPage = useCallback(
    (offset: number, _limit?: number): Promise<void> => {
      if (pageCacheRef.current.has(offset)) return Promise.resolve();
      return new Promise<void>((resolve) => {
        const list = pendingResolversRef.current.get(offset) ?? [];
        list.push(resolve);
        pendingResolversRef.current.set(offset, list);
        setActiveSkip(offset);
      });
    },
    [],
  );

  const refetch = useCallback(() => {
    pageCacheRef.current.clear();
    setMeta({ total: 0, hasMore: false });
    setActiveSkip(0);
    // Back to the skeleton: a refetch drops every row on purpose, so "known
    // empty" no longer holds until the fresh page answers.
    setSettled(false);
    setRefetchVersion((v) => v + 1);
    // The cache is empty, so the resolver at offset 0 fires on the fresh read
    // rather than on a cache hit. Registering it here — not through fetchPage —
    // is what lets the version bump above be the thing that reloads.
    return new Promise<void>((resolve) => {
      const list = pendingResolversRef.current.get(0) ?? [];
      list.push(resolve);
      pendingResolversRef.current.set(0, list);
    });
  }, []);

  return {
    items,
    total: meta.total,
    hasMore: meta.hasMore,
    // Initial-load only, and only while NOTHING has answered: once any result
    // lands (a cache hit or an empty page), the list renders what it knows and
    // the network refresh finishes silently in the background.
    loading: loading && !settled,
    loadingMore,
    fetchPage,
    refetch,
  };
}
